import axios from 'axios';
import moment from 'moment';

/**
 * TrueData Service — BYOD (Bring Your Own Data) Provider
 * 
 * TrueData provides real-time and historical market data for Indian markets
 * via WebSocket and REST APIs. Users bring their own TrueData subscription
 * credentials (username + password) to connect.
 * 
 * API Docs: https://truedata.in/api-documentation/
 * WebSocket: Real-time tick data via TrueData WebSocket feed
 * Historical: REST API for historical OHLCV data
 */

const API_BASE = process.env.VUE_APP_API_BASE || '';

// Timeframe mapping: app timeframes → TrueData bar sizes (in seconds)
const TIMEFRAME_MAP = {
    '1m': 60,
    '3m': 180,
    '5m': 300,
    '10m': 600,
    '15m': 900,
    '30m': 1800,
    '1H': 3600,
    '2H': 7200,
    '4H': 14400,
    '1D': 'EOD',
    '1W': 'EOD',   // Aggregate from daily
    '1M': 'EOD'    // Aggregate from daily
};

// Supported timeframes for UI
export const TRUEDATA_SUPPORTED_TFS = ['1m', '3m', '5m', '10m', '15m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];

export default {
    accessToken: null,
    username: null,
    _pendingState: null,
    ws: null,
    symbols: [],
    reconnectAttempts: 0,
    _wsOnMessage: null,
    _wsOnError: null,
    _wsSymbols: null,
    _wsAuthenticated: false,
    _heartbeatInterval: null,

    // ─── Authentication (Credential-based — BYOD) ───

    /**
     * Get auth URL — for TrueData, this triggers credential entry flow.
     * Returns a state token for the credential submission.
     */
    async getAuthUrl() {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/truedata/auth-url', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.data && response.data.success) {
            this._pendingState = response.data.state;
            return response.data.authUrl || 'credential-entry';
        }
        throw new Error(response.data?.error || 'Failed to initiate TrueData connection');
    },

    /**
     * Complete login — validates TrueData credentials with the server.
     */
    async completeLogin(state) {
        const stateToUse = state || this._pendingState;
        if (!stateToUse) {
            throw new Error('No pending TrueData auth state');
        }

        const token = await this._getClerkToken();
        const response = await axios.post(API_BASE + '/api/truedata/complete',
            { state: stateToUse },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (response.data && response.data.success) {
            this.accessToken = response.data.accessToken || response.data.sessionId;
            this.username = response.data.username;

            localStorage.setItem('truedata_access_token', this.accessToken);
            localStorage.setItem('truedata_username', this.username);

            this._pendingState = null;
            return { success: true, accessToken: this.accessToken, username: this.username };
        }

        throw new Error(response.data?.error || 'Failed to complete TrueData login');
    },

    /**
     * Restore session from server.
     */
    async restoreSessionFromServer() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/truedata/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.username = response.data.username;
                localStorage.setItem('truedata_access_token', this.accessToken);
                localStorage.setItem('truedata_username', this.username);
                return true;
            }
        } catch (error) {
            console.warn('[TrueData] Could not restore session:', error.message);
        }
        return false;
    },

    async validateSession() {
        if (!this.isAuthenticated()) {
            return { valid: false, expired: false, neverConnected: true };
        }
        try {
            const token = await this._getClerkToken();
            if (!token) return { valid: false, expired: false };

            const response = await axios.get(API_BASE + '/api/truedata/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected) {
                return { valid: true, expired: false };
            }
            return { valid: false, expired: true };
        } catch (error) {
            return { valid: false, expired: false };
        }
    },

    async disconnectFromServer() {
        try {
            const token = await this._getClerkToken();
            if (token) {
                await axios.post(API_BASE + '/api/truedata/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.warn('[TrueData] Could not clear session:', error.message);
        }
        this.clearSession();
    },

    async _getClerkToken() {
        try {
            if (window.__vue_clerk && window.__vue_clerk.getToken) {
                return await window.__vue_clerk.getToken();
            }
            if (window.Clerk && window.Clerk.session) {
                return await window.Clerk.session.getToken();
            }
            if (window.Clerk && window.Clerk.getToken) {
                return await window.Clerk.getToken();
            }
        } catch (e) {
            console.warn('[TrueData] Could not get Clerk token:', e.message);
        }
        return null;
    },

    isAuthenticated() {
        return !!(this.accessToken || localStorage.getItem('truedata_access_token'));
    },

    loadSavedSession() {
        this.accessToken = localStorage.getItem('truedata_access_token');
        this.username = localStorage.getItem('truedata_username');
        return this.accessToken;
    },

    clearSession() {
        this.accessToken = null;
        this.username = null;
        this._pendingState = null;
        localStorage.removeItem('truedata_access_token');
        localStorage.removeItem('truedata_username');
        this.closeWebSocket();
    },

    async getProfile() {
        try {
            const token = await this._getClerkToken();
            const response = await axios.get(API_BASE + '/api/truedata/profile', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // ─── Symbol Search ───

    async searchSymbols(query, limit = 30) {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/truedata/search', {
                params: { query: query.toUpperCase(), limit },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
            return [];
        } catch (error) {
            console.error('[TrueData] search error:', error);
            return [];
        }
    },

    async loadSymbolMaster(exchange = 'NSE') {
        return this.searchSymbols('A', 100);
    },

    // ─── Historical Data ───

    async loadHistoricalData(symbol, timeframe, _retryCount) {
        const retryCount = _retryCount || 0;

        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with TrueData');
        }

        if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
            throw new Error('Symbol is required');
        }

        const resolution = TIMEFRAME_MAP[timeframe];
        if (!resolution) {
            throw new Error(`Invalid timeframe: ${timeframe}`);
        }

        const toDate = moment().format('YYYY-MM-DD HH:mm:ss');
        const fromDate = this._calculateFromDate(timeframe);

        try {
            const token = await this._getClerkToken();
            if (!token) throw new Error('Not authenticated');

            const response = await axios.get(API_BASE + '/api/truedata/history', {
                params: {
                    symbol: symbol.trim(),
                    resolution: String(resolution),
                    from_date: fromDate,
                    to_date: toDate
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success && response.data.data) {
                let candles = response.data.data;

                // Normalize to [timestamp, open, high, low, close, volume]
                if (candles.length > 0 && !Array.isArray(candles[0])) {
                    candles = candles.map(c => [
                        moment(c.time || c.timestamp || c.date).valueOf(),
                        Number(c.open), Number(c.high), Number(c.low), Number(c.close),
                        Number(c.volume || 0)
                    ]);
                }

                candles.sort((a, b) => a[0] - b[0]);

                // Aggregate weekly/monthly from daily
                if (timeframe === '1W') candles = this._aggregatePeriod(candles, 'week');
                if (timeframe === '1M') candles = this._aggregatePeriod(candles, 'month');

                return candles;
            }
            throw new Error(response.data?.error || 'Failed to load data');
        } catch (error) {
            const status = error.response && error.response.status;
            if (status === 401) {
                this.clearSession();
                throw new Error('TrueData session expired. Please reconnect.');
            }
            if (retryCount < 2 && (status === 429 || (status >= 500 && status < 600))) {
                await new Promise(r => setTimeout(r, (retryCount + 1) * 2000));
                return this.loadHistoricalData(symbol, timeframe, retryCount + 1);
            }
            throw error;
        }
    },

    _calculateFromDate(timeframe) {
        const now = moment();
        const OPTIMAL_DAYS = {
            '1m': 5, '3m': 10, '5m': 10, '10m': 15, '15m': 25,
            '30m': 45, '1H': 60, '2H': 75, '4H': 90,
            '1D': 365, '1W': 365 * 3, '1M': 365 * 5
        };
        const days = OPTIMAL_DAYS[timeframe] || 30;
        return now.clone().subtract(days, 'days').format('YYYY-MM-DD HH:mm:ss');
    },

    _aggregatePeriod(candles, period) {
        if (!candles || candles.length === 0) return [];
        const aggregated = [];
        let currentGroup = [];
        let currentPeriod = null;

        for (const candle of candles) {
            const date = moment(candle[0]);
            const key = period === 'week' ? date.format('YYYY-WW') : date.format('YYYY-MM');

            if (key !== currentPeriod) {
                if (currentGroup.length > 0) aggregated.push(this._combineGroup(currentGroup));
                currentGroup = [candle];
                currentPeriod = key;
            } else {
                currentGroup.push(candle);
            }
        }
        if (currentGroup.length > 0) aggregated.push(this._combineGroup(currentGroup));
        return aggregated;
    },

    _combineGroup(group) {
        if (group.length === 0) return null;
        if (group.length === 1) return group[0];
        const first = group[0], last = group[group.length - 1];
        let high = first[2], low = first[3], volume = 0;
        for (const c of group) {
            high = Math.max(high, c[2]);
            low = Math.min(low, c[3]);
            volume += c[5] || 0;
        }
        return [first[0], first[1], high, low, last[4], volume];
    },

    // ─── WebSocket ───

    async connectWebSocket(symbols, onMessage) {
        if (!this.isAuthenticated()) return null;
        this.closeWebSocket();

        try {
            const token = await this._getClerkToken();
            if (!token) return null;

            const response = await axios.get(API_BASE + '/api/truedata/ws-url', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data || !response.data.success || !response.data.wsUrl) return null;

            const wsUrl = response.data.wsUrl;
            const authPayload = response.data.authPayload;

            this._wsSymbols = symbols;
            this._wsOnMessage = onMessage;

            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = () => {
                this.reconnectAttempts = 0;
                if (authPayload) this.ws.send(JSON.stringify(authPayload));
            };

            this.ws.onmessage = (event) => {
                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : null;
                    if (!data) return;

                    // Handle auth response
                    if (data.success !== undefined) {
                        if (data.success) {
                            this._wsAuthenticated = true;
                            if (symbols && symbols.length) this._subscribeSymbols(symbols);
                            // Start heartbeat
                            this._heartbeatInterval = setInterval(() => {
                                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                                    this.ws.send(JSON.stringify({ method: 'heartbeat' }));
                                }
                            }, 30000);
                        }
                        return;
                    }

                    if (onMessage) {
                        const normalized = this._normalizeWsMessage(data);
                        if (normalized) onMessage(normalized);
                    }
                } catch (error) {
                    console.error('[TrueData] WS message error:', error);
                }
            };

            this.ws.onerror = (error) => {
                if (this._wsOnError) this._wsOnError(error);
            };

            this.ws.onclose = (event) => {
                if (event.code === 1000 || !this.isAuthenticated()) return;
                if (this.reconnectAttempts < 5) {
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    this.reconnectAttempts++;
                    setTimeout(() => this.connectWebSocket(this._wsSymbols, this._wsOnMessage), delay);
                }
            };

            return this.ws;
        } catch (error) {
            console.error('[TrueData] WebSocket error:', error);
            return null;
        }
    },

    _subscribeSymbols(symbols) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        const msg = { method: 'addsymbol', symbols: symbols };
        this.ws.send(JSON.stringify(msg));
    },

    subscribeToSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify({ method: 'addsymbol', symbols: [symbol] }));
    },

    unsubscribeFromSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        this.ws.send(JSON.stringify({ method: 'removesymbol', symbols: [symbol] }));
    },

    closeWebSocket() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close(1000, 'Manual close');
            this.ws = null;
        }
        this.reconnectAttempts = 0;
        this._wsSymbols = null;
        this._wsAuthenticated = false;
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    },

    destroyWs() { this.closeWebSocket(); },

    createWebSocket(onMessage, onError, symbols) {
        this._wsOnError = onError;
        return this.connectWebSocket(symbols || null, onMessage);
    },

    _normalizeWsMessage(data) {
        if (!data) return null;
        // TrueData tick: { symbol, ltp, timestamp, volume, ... }
        if (data.ltp || data.LTP) {
            return {
                symbol: data.symbol || data.Symbol || '',
                ltp: Number(data.ltp || data.LTP) || 0,
                timestamp: data.timestamp ? moment(data.timestamp).valueOf() : Date.now(),
                volume: Number(data.volume || data.Volume) || 0,
                close_price: Number(data.close || data.prev_close) || 0,
                type: 'truedata_tick'
            };
        }
        return data;
    },

    async checkAuthStatus() {
        try {
            const token = await this._getClerkToken();
            if (!token) return { success: false };
            const response = await axios.get(API_BASE + '/api/truedata/session', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data && response.data.connected) {
                this.accessToken = response.data.accessToken;
                this.username = response.data.username;
                localStorage.setItem('truedata_access_token', this.accessToken);
                localStorage.setItem('truedata_username', this.username);
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            return { success: false };
        }
    }
};
